/**
 * Expect value or else throw
 * This enables us to check a value existes and return a type without the posibility of undefined or null.
 */

import mime from "mime";
import { TRPCError } from "@trpc/server";
import { type TRPC_ERROR_CODE_KEY } from "@trpc/server/rpc";
import { z } from "zod";
import _ from "lodash";

/**
 * Throw an error with stack trace added
 * @param error
 * @returns
 */
export const TRPCErrorWithStack = (error: {
  code: TRPC_ERROR_CODE_KEY;
  message: string;
  cause?: string | object;
}) => {
  return new TRPCError({
    code: error?.code || "NOT_FOUND",
    message: `Msg: ${error?.message || ""} - Code: ${error?.code}`,
    cause: error?.cause,
  });
};

/**
 * Throws an error on backend if the provided object is `undefined` or `null`, otherwise returns the object.
 *
 * @template T - The type of the object.
 * @param object - The object to check.
 * @param - Optional error details.
 * @returns - The object if it is not `undefined` or `null`.
 * @throws - If the object is `undefined` or `null`.
 */
export const expectOrThrow = <T>(
  object: T,
  error?: { code?: TRPC_ERROR_CODE_KEY; message: string; cause?: string },
): Exclude<T, undefined | null> => {
  if (object === undefined || object === null) {
    throw TRPCErrorWithStack({
      code: error?.code || "NOT_FOUND",
      message: error?.message || "Not found",
      cause: error?.cause,
    });
  } else {
    return object as Exclude<T, undefined | null>;
  }
};

/**
 * Isolate leoId from leo email
 */
export const leoIdFromEmail = (leoEmail: string) => {
  return z.string().parse(leoEmail.toLowerCase().split("@leo-pharma.com")[0]);
};

/**
 * Generate a pretty file type from either filename or a supplied mimeType
 */
export const prettyMimeType = (fileName: string, mimeType?: string) => {
  try {
    const mt = mimeType || mime.getType(fileName);
    return mt ? mt.split("/")[1] : "Unknown";
  } catch {
    return "Unknown";
  }
};

/**
 * Plural s
 */

export const pluralS = (count: number) => (count === 1 ? "" : "s");

/**
 * Check that device is android
 */

export const isAndroid = () => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * Move element in an array
 */

export const moveArrayElement = <T>(
  arr: Array<T>,
  from: number,
  to: number,
) => {
  const copy = [...arr];
  const [removed] = copy.splice(from, 1);
  copy.splice(to, 0, removed!);
  return copy;
};

/**
 * Compare objects and return the two object with the differences
 */
export const compareObjects = (
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>,
  excludeKeys?: string[],
) => {
  // Loop through the keys of add properties where values are different
  const oldDataFiltered: Record<string, unknown> = {};
  const newDataFiltered: Record<string, unknown> = {};
  Object.keys(oldData).forEach((key) => {
    if (excludeKeys?.includes(key)) return;
    if (_.isEqual(oldData[key], newData[key])) return;
    oldDataFiltered[key] = oldData[key];
    newDataFiltered[key] = newData[key];
  });

  return { oldDataFiltered, newDataFiltered };
};

export const safeAwait = async <T>(
  promise: Promise<T>,
): Promise<[null, Error] | [T, null]> => {
  try {
    const data = await promise;
    return [data, null];
  } catch (error) {
    return [null, error instanceof Error ? error : new Error(String(error))];
  }
};

/**
 * Extracts message from nested Azure error structure: { error: { message: "..." } }
 * @param objErr - The error object to check
 * @returns The message string if found, null otherwise
 */
const extractAzureErrorMessage = (
  objErr: Record<string, unknown>,
): string | null => {
  if (!objErr.error || typeof objErr.error !== "object") {
    return null;
  }

  const errorObj = objErr.error as Record<string, unknown>;
  if (!("message" in errorObj)) {
    return null;
  }

  if (typeof errorObj.message !== "string") {
    return null;
  }

  return errorObj.message;
};

/**
 * Extract a string error message from an unknown error type.
 * Handles Error objects, strings, and plain objects with common error properties.
 *
 * @param err - The unknown error to extract a message from
 * @returns A string representation of the error
 */
export const extractErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message || err.name || "Error (no message)";
  }
  if (typeof err === "string") {
    return err || "Empty string error";
  }
  if (err && typeof err === "object") {
    const objErr = err as Record<string, unknown>;

    // Check for nested Azure error structure: { error: { message: "..." } }
    const azureMessage = extractAzureErrorMessage(objErr);
    if (azureMessage) {
      return azureMessage;
    }

    if (typeof objErr.message === "string" && objErr.message) {
      return objErr.message;
    }
    if (typeof objErr.error === "string" && objErr.error) {
      return objErr.error;
    }
    if (typeof objErr.name === "string" && objErr.name) {
      return objErr.name;
    }
    try {
      const jsonStr = JSON.stringify(err, Object.getOwnPropertyNames(err));
      if (jsonStr && jsonStr !== "{}" && jsonStr !== "[]") {
        return jsonStr;
      }
    } catch {
      // Handle circular references or other serialization errors
      return "Error (serialization failed)";
    }
  }
  return "Unknown error";
};

/**
 * Error message for AI connection issues (timeouts, resets).
 * Used across AI services (chat-ai, image-ai) for consistent user-facing messages.
 */
export const AI_CONNECTION_ERROR_MESSAGE =
  "The AI is currently experiencing connection issues. Please try again later.";

/**
 * Check if an error message indicates a connection error (ETIMEDOUT, ECONNRESET).
 */
export const isConnectionError = (errorMessage: string): boolean => {
  return (
    errorMessage.includes("ETIMEDOUT") || errorMessage.includes("ECONNRESET")
  );
};

/**
 * Redacts sensitive session data (access tokens) for logging purposes.
 */
export function redactSession<T extends { user: { access_token?: string } }>(
  session: T | null,
): T | null {
  if (!session) return null;
  return {
    ...session,
    user: { ...session.user, access_token: "[REDACTED]" },
  };
}
